import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkle, ArrowsClockwise, Lightning, Lock } from '@phosphor-icons/react';
import { hasTier } from '../../context/SubscriptionContext';
import PickCard from './PickCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NextDayPicksSection({ user, tier }) {
  const [picks, setPicks] = useState([]);
  const [picksLoading, setPicksLoading] = useState(false);
  const [picksGeneratedAt, setPicksGeneratedAt] = useState(null);

  const loadPicks = useCallback(async () => {
    if (!hasTier(tier, 'pro')) return;
    setPicksLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/next-day-picks`, {}, { withCredentials: true });
      setPicks(data.picks || []);
      setPicksGeneratedAt(data.generated_at);
    } catch (e) {
      console.error('next-day-picks fetch failed', e);
      setPicks([]);
    } finally {
      setPicksLoading(false);
    }
  }, [tier]);

  useEffect(() => {
    if (user && hasTier(tier, 'pro')) loadPicks();
  }, [user, tier, loadPicks]);

  const hasProAccess = hasTier(tier, 'pro');
  const isInitialLoading = picksLoading && picks.length === 0;

  return (
    <div className="bg-[#12141A] border border-[#A855F7]/30 p-6 mb-6" data-testid="next-day-picks-section">
      <Header loading={picksLoading} onRefresh={loadPicks} showRefreshButton={hasProAccess} />

      {!hasProAccess && <LockedState />}
      {hasProAccess && isInitialLoading && <LoadingState />}

      {picks.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="picks-grid">
            {picks.map((pick) => <PickCard key={pick.symbol} pick={pick} />)}
          </div>
          {picksGeneratedAt && (
            <div className="text-[10px] font-mono text-[#64748B] mt-4 flex items-center gap-2">
              <span>Generated: {new Date(picksGeneratedAt).toLocaleString('en-IN')}</span>
              <span>•</span>
              <span>Not investment advice. Verify before trading.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Header({ loading, onRefresh, showRefreshButton }) {
  return (
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <div className="flex items-center gap-2">
        <Lightning size={28} weight="fill" className="text-[#FBBF24]" />
        <div>
          <h2 className="text-2xl font-heading font-bold tracking-tight text-white">Next Day Movers</h2>
          <p className="text-xs text-[#94A3B8] mt-0.5">
            AI-picked Nifty 50 stocks with 2-20% upside potential for tomorrow's session
          </p>
        </div>
      </div>
      {showRefreshButton && (
        <button
          onClick={onRefresh}
          disabled={loading}
          className="bg-[#A855F7] hover:bg-purple-600 text-white px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
          data-testid="refresh-picks-btn"
        >
          <ArrowsClockwise size={16} weight="bold" className={loading ? 'animate-spin' : ''} />
          {loading ? 'Analyzing...' : 'Refresh Picks'}
        </button>
      )}
    </div>
  );
}

function LockedState() {
  return (
    <div className="py-10 text-center border border-dashed border-[#A855F7]/30" data-testid="next-day-locked">
      <Lock size={40} weight="duotone" className="text-[#A855F7] mx-auto mb-3" />
      <p className="text-sm text-[#94A3B8] mb-4">
        Next Day Movers is a <span className="text-[#A855F7] font-semibold">Pro</span> feature
      </p>
      <Link
        to="/pricing?target=pro"
        className="inline-block bg-[#A855F7] hover:bg-purple-600 text-white px-6 py-2 text-sm font-medium transition-colors"
        data-testid="upgrade-next-day"
      >
        Upgrade to unlock →
      </Link>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="text-center py-8" data-testid="picks-loading">
      <Sparkle size={32} weight="duotone" className="animate-pulse text-[#A855F7] mx-auto mb-3" />
      <p className="text-sm text-[#94A3B8]">GPT-5.2 is scanning all 50 Nifty stocks...</p>
    </div>
  );
}
